import { backendUrl } from './links';

/**
 * X-TOL-Client header for requests to the TOL backend API.
 *
 * Format (backend #136, serverActiveService.js):
 *   <platform>/<app_version>/<update_id>
 * where each part is 1–64 characters of [A-Za-z0-9._+-].
 *
 * The web app sends `web/<version>/none`. <version> is the short commit SHA
 * baked in at build time as REACT_APP_GIT_SHA (see the `build` script in
 * package.json: REACT_APP_GIT_SHA, else Heroku's SOURCE_VERSION, else GitLab's
 * CI_COMMIT_SHORT_SHA), falling back to the package.json version.
 *
 * The header is added by a single fetch wrapper, and only for URLs under the
 * configured backend base URL. Mixpanel, Discord, Supabase and other
 * third-party requests are left untouched, and a header a call already sets is
 * never overwritten.
 */
export const TOL_CLIENT_HEADER = 'X-TOL-Client';
export const TOL_CLIENT_PLATFORM = 'web';
export const TOL_CLIENT_UPDATE_ID = 'none';
export const FALLBACK_APP_VERSION = '0.1.0';

const PART_MAX = 64;
const FULL_SHA = /^[0-9a-f]{40}$/i;

/** Keep only [A-Za-z0-9._+-], cap at 64 characters. Returns '' when nothing is left. */
export const sanitizePart = (value) =>
  String(value == null ? '' : value)
    .trim()
    .replace(/[^A-Za-z0-9._+-]/g, '')
    .slice(0, PART_MAX);

/** Short SHA for a full 40-char SHA, otherwise the sanitized value; falls back to the package version. */
export const resolveAppVersion = (
  gitSha = process.env.REACT_APP_GIT_SHA,
  packageVersion = process.env.REACT_APP_VERSION
) => {
  const raw = String(gitSha == null ? '' : gitSha).trim();
  const sha = sanitizePart(FULL_SHA.test(raw) ? raw.slice(0, 8) : raw);
  if (sha) return sha;
  return sanitizePart(packageVersion) || FALLBACK_APP_VERSION;
};

export const buildTolClientValue = (version = resolveAppVersion()) =>
  `${TOL_CLIENT_PLATFORM}/${sanitizePart(version) || FALLBACK_APP_VERSION}/${TOL_CLIENT_UPDATE_ID}`;

export const TOL_CLIENT_VALUE = buildTolClientValue();

const currentOrigin = () => {
  try {
    return window.location.origin;
  } catch {
    return 'http://localhost';
  }
};

const toUrl = (value, base) => {
  try {
    return new URL(String(value), base);
  } catch {
    return null;
  }
};

const requestUrl = (input) => {
  if (input == null) return '';
  if (typeof input === 'string') return input;
  if (typeof input.url === 'string') return input.url; // Request
  return String(input); // URL or anything with toString
};

/** True only for URLs on the backend's origin and under its base path. */
export const isBackendUrl = (input, base = backendUrl) => {
  if (!base) return false;
  const origin = currentOrigin();
  const baseUrl = toUrl(base, origin);
  const target = toUrl(requestUrl(input), origin);
  if (!baseUrl || !target) return false;
  if (target.origin !== baseUrl.origin) return false;
  const basePath = baseUrl.pathname.replace(/\/+$/, '');
  if (!basePath) return true;
  return target.pathname === basePath || target.pathname.startsWith(`${basePath}/`);
};

const hasHeader = (headers, name) => {
  if (!headers) return false;
  const lower = name.toLowerCase();
  if (typeof headers.has === 'function') return headers.has(name);
  if (Array.isArray(headers)) return headers.some((pair) => String(pair && pair[0]).toLowerCase() === lower);
  return Object.keys(headers).some((key) => key.toLowerCase() === lower);
};

const withHeader = (headers, name, value) => {
  if (!headers) return { [name]: value };
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const copy = new Headers(headers);
    copy.set(name, value);
    return copy;
  }
  if (Array.isArray(headers)) return [...headers, [name, value]];
  return { ...headers, [name]: value };
};

/**
 * Returns the init to pass to fetch: unchanged for non-backend URLs or when
 * the header is already set (on init.headers or on a Request input).
 */
export const addTolClientHeader = (input, init, { base = backendUrl, value = TOL_CLIENT_VALUE } = {}) => {
  if (!isBackendUrl(input, base)) return init;
  const initHeaders = init && init.headers;
  if (hasHeader(initHeaders, TOL_CLIENT_HEADER)) return init;
  const isRequest = input && typeof input === 'object' && typeof input.url === 'string' && input.headers;
  if (!initHeaders && isRequest) {
    // init.headers would replace the Request's own headers, so carry them over.
    if (hasHeader(input.headers, TOL_CLIENT_HEADER)) return init;
    const copy = typeof Headers !== 'undefined' ? new Headers(input.headers) : { ...input.headers };
    return { ...(init || {}), headers: withHeader(copy, TOL_CLIENT_HEADER, value) };
  }
  return { ...(init || {}), headers: withHeader(initHeaders, TOL_CLIENT_HEADER, value) };
};

const WRAPPED = '__tolClientWrapped';

/** Wraps the global fetch once. Never throws; if anything goes wrong the original call is used. */
export const installTolClientFetch = (target = typeof window !== 'undefined' ? window : undefined, options) => {
  if (!target || typeof target.fetch !== 'function' || target.fetch[WRAPPED]) return false;
  const originalFetch = target.fetch;
  const wrapped = function tolClientFetch(input, init) {
    let nextInit = init;
    try {
      nextInit = addTolClientHeader(input, init, options);
    } catch {
      nextInit = init;
    }
    // Always call with the original receiver (window.fetch throws "Illegal invocation" otherwise).
    return arguments.length < 2 && nextInit === init
      ? originalFetch.call(target, input)
      : originalFetch.call(target, input, nextInit);
  };
  wrapped[WRAPPED] = true;
  target.fetch = wrapped;
  return true;
};
